import {
  galleryLightboxClass,
  resolveGalleryLightbox,
  type GalleryLightbox,
  type LayoutBlock,
} from '@web-studio/shared-schemas';
import {
  galleryArrangementClass,
  readGalleryFrameSlots,
  resolveGalleryArrangement,
  type GalleryFrameSlot,
} from '../blocks/gallery/galleryFrames';
import {
  LUCIDE_SSR_CHEVRON_LEFT,
  LUCIDE_SSR_CHEVRON_RIGHT,
  LUCIDE_SSR_X,
} from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickBlockField } from '../lib/localized';
import { pickChrome } from '../lib/siteChromeCopy';
import { escapeHtml } from './escape';

const FRAME_WIDTHS = [480, 720, 960, 1280];

function padIndex(index: number): string {
  return String(index).padStart(2, '0');
}

function imgTag(options: {
  rawUrl: string;
  alt: string;
  sizes: string;
  className: string;
  width: number;
  height: number;
}): string {
  const src = buildSiteImageProxyUrl(options.rawUrl, options.width, 78);
  const srcSet = buildSiteImageSrcSet(options.rawUrl, FRAME_WIDTHS, 78);
  return `<img class="${options.className}" src="${escapeHtml(src)}" srcset="${escapeHtml(srcSet)}" sizes="${escapeHtml(options.sizes)}" alt="${escapeHtml(options.alt)}" width="${options.width}" height="${options.height}" loading="lazy" decoding="async" />`;
}

function lightboxSlideAttrs(slot: GalleryFrameSlot, alt: string): string {
  const src = buildSiteImageProxyUrl(slot.rawUrl!, 1280, 78);
  const srcSet = buildSiteImageSrcSet(slot.rawUrl!, FRAME_WIDTHS, 78);
  const caption = slot.caption ?? '';
  return `data-ws-g-src="${escapeHtml(src)}" data-ws-g-srcset="${escapeHtml(srcSet)}" data-ws-g-alt="${escapeHtml(alt)}" data-ws-g-caption="${escapeHtml(caption)}" data-ws-g-i="${slot.index}"`;
}

function hitHtml(slot: GalleryFrameSlot, groupId: string, alt: string, sizes: string): string {
  const preview = imgTag({
    rawUrl: slot.rawUrl!,
    alt,
    sizes,
    className: 'ws-gallery__img',
    width: 960,
    height: 720,
  });
  return `<button type="button" class="ws-gallery__hit" popovertarget="${escapeHtml(`${groupId}-lb`)}" ${lightboxSlideAttrs(slot, alt)} aria-label="${escapeHtml(alt)}"><span class="ws-gallery__media">${preview}</span></button>`;
}

function iconHtml(
  node: typeof LUCIDE_SSR_X,
  size: 'sm' | 'md' | 'lg',
): string {
  return renderLucideIconHtml(node.iconNode, { size, name: node.name });
}

function viewerHtml(options: {
  slots: GalleryFrameSlot[];
  groupId: string;
  sectionTitle: string;
  look: GalleryLightbox;
  closeLabel: string;
  prevLabel: string;
  nextLabel: string;
  goToPhotoLabel: string;
}): string {
  const { slots, groupId, look } = options;
  if (slots.length === 0) return '';
  const showNav = slots.length > 1;
  const first = slots[0]!;
  const alt = first.caption || options.sectionTitle || `Photo ${first.index}`;
  const photoTitle = first.caption || alt;
  const popoverId = `${groupId}-lb`;
  const closeIcon = iconHtml(LUCIDE_SSR_X, 'md');
  const navSize = look === 'cinema' ? 'lg' : 'md';
  const prevIcon = iconHtml(LUCIDE_SSR_CHEVRON_LEFT, navSize);
  const nextIcon = iconHtml(LUCIDE_SSR_CHEVRON_RIGHT, navSize);
  const full = imgTag({
    rawUrl: first.rawUrl!,
    alt,
    sizes: SITE_IMAGE_SIZES.galleryLightbox,
    className: 'ws-gallery__lightbox-img',
    width: 1280,
    height: 960,
  });
  const close = `<button type="button" class="ws-gallery__close" popovertarget="${escapeHtml(popoverId)}" popovertargetaction="hide" aria-label="${escapeHtml(options.closeLabel)}">${closeIcon}</button>`;
  const nav = showNav
    ? `<button type="button" class="ws-gallery__nav ws-gallery__nav--prev" data-ws-g-step="-1" aria-label="${escapeHtml(options.prevLabel)}">${prevIcon}</button><button type="button" class="ws-gallery__nav ws-gallery__nav--next" data-ws-g-step="1" aria-label="${escapeHtml(options.nextLabel)}">${nextIcon}</button>`
    : '';
  const captionText = first.caption ?? '';
  const caption = `<p class="ws-gallery__lightbox-caption"${captionText ? '' : ' hidden'}>${escapeHtml(captionText)}</p>`;
  const lightboxModifier = galleryLightboxClass(look);
  const shellClass = lightboxModifier
    ? `ws-gallery__lightbox ${lightboxModifier}`
    : 'ws-gallery__lightbox';
  const shellOpen = `<div id="${escapeHtml(popoverId)}" class="${shellClass}" data-lightbox="${look}" data-ws-g-active="${first.index}" popover="auto" role="dialog" aria-label="${escapeHtml(alt)}">`;

  let dialog = `<div class="ws-gallery__dialog">${close}${nav}${full}${caption}</div>`;
  if (look === 'catalog') {
    const title = `<p class="ws-gallery__sheet-title"${photoTitle ? '' : ' hidden'}>${escapeHtml(photoTitle)}</p>`;
    const thumbs = slots
      .map((slot) => {
        const thumbAlt = slot.caption || options.sectionTitle || `Photo ${slot.index}`;
        const thumb = imgTag({
          rawUrl: slot.rawUrl!,
          alt: '',
          sizes: SITE_IMAGE_SIZES.galleryThumb,
          className: 'ws-gallery__sheet-thumb-img',
          width: 240,
          height: 180,
        });
        const current = slot.index === first.index ? ' aria-current="true"' : '';
        const label = options.goToPhotoLabel.replace('{n}', String(slot.index));
        return `<li><button type="button" class="ws-gallery__sheet-thumb" ${lightboxSlideAttrs(slot, thumbAlt)}${current} aria-label="${escapeHtml(label)}">${thumb}</button></li>`;
      })
      .join('');
    const rail = showNav ? `<ul class="ws-gallery__sheet-thumbs">${thumbs}</ul>` : '';
    dialog = `<div class="ws-gallery__dialog"><header class="ws-gallery__sheet-head">${title}${close}</header><div class="ws-gallery__sheet-stage"><div class="ws-gallery__sheet-photo">${nav}${full}</div></div>${rail}</div>`;
  } else if (look === 'cinema') {
    const count = `<p class="ws-gallery__lightbox-count"><span class="ws-gallery__lightbox-index">${padIndex(1)}</span><span class="ws-gallery__lightbox-total"> / ${padIndex(slots.length)}</span></p>`;
    dialog = `<div class="ws-gallery__dialog">${close}${nav}${full}<div class="ws-gallery__lightbox-meta">${count}${caption}</div></div>`;
  }

  return `<div class="ws-gallery__viewer">${shellOpen}${dialog}</div></div>`;
}

export function renderImageGallery(block: LayoutBlock, locale: string): string {
  const arrangement = resolveGalleryArrangement(block.registryBlockKey);
  const lightboxLook = resolveGalleryLightbox(block.registryBlockKey, block.content);
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const slots = readGalleryFrameSlots(block, locale);
  const groupId = `gal-${block.blockId}`;
  const titleId = `${groupId}-title`;
  const closeLabel = pickChrome('galleryClose', locale);
  const prevLabel = pickChrome('galleryPrev', locale);
  const nextLabel = pickChrome('galleryNext', locale);
  const filmstripNavLabel = pickChrome('galleryFilmstripNav', locale);
  const goToPhotoLabel = pickChrome('galleryGoToPhoto', locale);
  const modifier = galleryArrangementClass(arrangement);
  const registryAttr = block.registryBlockKey
    ? ` data-registry-key="${escapeHtml(block.registryBlockKey)}"`
    : '';
  const labelledBy = sectionTitle ? ` aria-labelledby="${escapeHtml(titleId)}"` : '';

  function sizesFor(position: number): string {
    if (arrangement === 'filmstrip') return SITE_IMAGE_SIZES.galleryFilmstrip;
    if (arrangement === 'ledger') return SITE_IMAGE_SIZES.galleryLedger;
    if (arrangement === 'spotlight') return SITE_IMAGE_SIZES.galleryThumb;
    if (arrangement === 'masonry') return SITE_IMAGE_SIZES.galleryMasonry;
    return position === 0 ? SITE_IMAGE_SIZES.galleryMosaicLead : SITE_IMAGE_SIZES.galleryMosaicCell;
  }

  const stage =
    arrangement === 'spotlight' && slots.length > 0
      ? `<div class="ws-gallery__stage">${slots
          .map((slot) => {
            const alt = slot.caption || sectionTitle || `Photo ${slot.index}`;
            const shot = imgTag({
              rawUrl: slot.rawUrl!,
              alt,
              sizes: SITE_IMAGE_SIZES.gallerySpotlight,
              className: 'ws-gallery__img',
              width: 1280,
              height: 960,
            });
            return `<div class="ws-gallery__stage-shot" data-index="${slot.index}"><button type="button" class="ws-gallery__hit" popovertarget="${escapeHtml(`${groupId}-lb`)}" ${lightboxSlideAttrs(slot, alt)} aria-label="${escapeHtml(alt)}"><span class="ws-gallery__media">${shot}</span></button></div>`;
          })
          .join('')}</div>`
      : '';

  const frames = slots
    .map((slot, position) => {
      const alt = slot.caption || sectionTitle || `Photo ${slot.index}`;
      const caption = slot.caption
        ? `<p class="ws-gallery__caption">${escapeHtml(slot.caption)}</p>`
        : '';
      const indexMark =
        arrangement === 'ledger' || arrangement === 'filmstrip' || arrangement === 'spotlight'
          ? `<span class="ws-gallery__index" aria-hidden="true">${padIndex(slot.index)}</span>`
          : '';

      if (arrangement === 'spotlight') {
        const thumb = imgTag({
          rawUrl: slot.rawUrl!,
          alt: '',
          sizes: SITE_IMAGE_SIZES.galleryThumb,
          className: 'ws-gallery__img',
          width: 240,
          height: 180,
        });
        const checked = position === 0 ? ' checked' : '';
        return `<li class="ws-gallery__frame" style="--ws-gallery-i:${position}" data-index="${slot.index}"><input class="ws-gallery__spot ws-gallery__control" type="radio" name="${escapeHtml(`${groupId}-spot`)}" id="${escapeHtml(`${groupId}-spot-${slot.index}`)}" data-index="${slot.index}"${checked} /><label class="ws-gallery__thumb" for="${escapeHtml(`${groupId}-spot-${slot.index}`)}"><span class="ws-gallery__media">${thumb}</span>${indexMark}</label>${caption}</li>`;
      }

      const showIndex = arrangement === 'ledger' || arrangement === 'filmstrip';
      const body = hitHtml(slot, groupId, alt, sizesFor(position));
      return `<li class="ws-gallery__frame" style="--ws-gallery-i:${position}" data-index="${slot.index}">${showIndex ? indexMark : ''}${body}${caption}</li>`;
    })
    .join('');

  const boardClass = arrangement === 'filmstrip' ? 'ws-gallery__scroller' : 'ws-gallery__board';
  const dots =
    arrangement === 'filmstrip' && slots.length > 1
      ? `<nav class="ws-gallery__dots" aria-label="${escapeHtml(filmstripNavLabel)}">${slots
          .map((slot, position) => {
            const label = goToPhotoLabel.replace('{n}', String(slot.index));
            const current = position === 0 ? ' aria-current="true"' : '';
            return `<button type="button" class="ws-gallery__dot" data-index="${slot.index}" aria-label="${escapeHtml(label)}"${current}></button>`;
          })
          .join('')}</nav>`
      : '';
  const viewer = viewerHtml({
    slots,
    groupId,
    sectionTitle,
    look: lightboxLook,
    closeLabel,
    prevLabel,
    nextLabel,
    goToPhotoLabel,
  });

  return `<section class="ws-gallery ${modifier}" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-arrangement="${arrangement}" data-lightbox="${lightboxLook}"${registryAttr}${labelledBy} id="gallery">
  <div class="ws-gallery__inner">
    <header class="ws-gallery__intro">
      ${sectionTitle ? `<h2 class="ws-gallery__title" id="${escapeHtml(titleId)}">${escapeHtml(sectionTitle)}</h2>` : ''}
      ${sectionSubtitle ? `<p class="ws-gallery__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}
    </header>
    ${stage}
    <div class="${boardClass}"><ul class="ws-gallery__frames">${frames}</ul></div>
    ${dots}
    ${viewer}
  </div>
</section>`;
}
